/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import {
    CacheType,
    createObservableDataAction,
    IAction,
    IActionContext,
    IActionInput,
    ICreateActionContext
} from '@msdyn365-commerce/core';
import { ArrayExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { ProductsDataActions, ProductSearchCriteria } from '@msdyn365-commerce/retail-proxy';
import { getCartState } from '@msdyn365-commerce/global-state';
import { searchByCriteriaAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import { getLocale } from '../../../shared-utils/attributes/locale';
import { getTemperatureZone } from '../../../shared-utils/attributes/get-temperature-zone';
import { getAttributeStringOrDefault } from '../../../shared-utils/attributes';
import { createInventoryAvailabilitySearchCriteria, DeliveryMode, IProductInventoryInformation, mapAggregatedProductInventoryInformation } from '../../../components/common/product-inventory-utils';
import { SimpleProductExt } from '../../../shared-utils/types';

export class GetCartWithExtendedProductsInput implements IActionInput {
    public getCacheKey = () => 'GetCartWithExtendedProducts';
    public getCacheObjectType = () => 'GetCartWithExtendedProducts';
    public dataCacheType = (): CacheType => 'none';
}

/**
 * CreateInput method for the GetCartWithExtendedProducts method.
 * @param inputData The input data passed to the createInput method.
 */
export const createGetCartWithExtendedProductsInput = (inputData: ICreateActionContext): GetCartWithExtendedProductsInput => {
    const { requestContext } = inputData;
    if (!requestContext.user.isAuthenticated) {
        throw new Error('Unable to get cart with extended products. User is not authenticated.');
    }

    return new GetCartWithExtendedProductsInput();
};

/**
 * The action method for the GetCartWithExtendedProducts data action.
 * @param input The action input.
 * @param ctx The action context.
 */
export async function getCartWithExtendedProductsAction(input: GetCartWithExtendedProductsInput, ctx: IActionContext): Promise<SimpleProductExt[]> {
    if (!ctx.requestContext.user.isAuthenticated) {
        throw new Error('Unable to get cart with extended products. User is not authenticated.');
    }

    const cart = await getCartState(ctx);

    if (!cart) {
        throw new Error('Unable to get cart with extended products. Cart is not available.');
    }

    const searchCriteriaInput: ProductSearchCriteria = {};
    searchCriteriaInput.Context = { ChannelId: ctx.requestContext.apiSettings.channelId, CatalogId: ctx.requestContext.apiSettings.catalogId };
    searchCriteriaInput.IncludeAttributes = true;
    searchCriteriaInput.SkipVariantExpansion = true;
    searchCriteriaInput.Ids = cart.cart.CartLines?.map(x => x.ProductId).filter(Boolean) as number[];

    const results = await searchByCriteriaAsync(
        {
            callerContext: ctx,
            queryResultSettings: {
                Paging: {
                    Skip: 0,
                    Top: 1000
                }
            }
        },
        searchCriteriaInput
    );

    if (!results) {
        return [];
    }

    const shippingSearchCriteria = createInventoryAvailabilitySearchCriteria(
        ctx,
        results.map(x => x.RecordId),
        true,
        undefined,
        undefined,
        DeliveryMode.shipping
    );
    const shippingAvailability = await ProductsDataActions.getEstimatedAvailabilityAsync(
        { callerContext: ctx },
        shippingSearchCriteria
    );
    let shippingInventory: IProductInventoryInformation[] | undefined;

    if (shippingAvailability.ProductWarehouseInventoryAvailabilities) {
        shippingInventory = mapAggregatedProductInventoryInformation(ctx, shippingAvailability);
        if (ArrayExtensions.hasElements(shippingInventory)) {
            shippingInventory[0].deliveryType = DeliveryMode.shipping;
        }
    }

    const attributeLocale = getLocale(ctx.requestContext.locale);
    const simpleProducts: SimpleProductExt[] = results?.map<SimpleProductExt>(x => (
        {
            AdjustedPrice: x.Price,
            AvailabilityLabel: shippingInventory?.find(y => y.ProductAvailableQuantity.ProductId === x.RecordId)?.StockLevelLabel,
            BasePrice: x.BasePrice || x.Price,
            Brand: getAttributeStringOrDefault(x.AttributeValues, attributeLocale.brand, ''),
            Description: x.Description,
            Price: x.Price,
            ProductTypeValue: x.RecordId,
            RecordId: x.RecordId,
            DefaultUnitOfMeasure: x.DefaultUnitOfMeasure,
            ItemId: x.ItemId,
            Name: x.Name,
            ProductNumber: x.ProductNumber,
            Storage: getTemperatureZone(ctx.requestContext.locale, x.AttributeValues)
        }
    ));

    return simpleProducts;
}

/**
 * The GetCartWithExtendedProducts Data Action
 * Returns order templates.
 */
export const getCartWithExtendedProductsActionDataAction = createObservableDataAction<SimpleProductExt[]>({
    id: '@msdyn365-commerce-modules/retail-actions/order-templates/get-cart-with-extended-products',
    action: <IAction<SimpleProductExt[]>>getCartWithExtendedProductsAction,
    input: createGetCartWithExtendedProductsInput
});

export default getCartWithExtendedProductsActionDataAction;
